/**
 * TemplateCard — one card in the template library grid (Figma "Templates —
 * Library", node 554:46136): an inset step-1 preview (subject + body,
 * straight from the template's own saved copy — no per-creator render call,
 * merge tags show literally) with the kind pill, and a footer with the
 * channel icon, name, the updated date under it, an archived tag, an "AI"
 * tag when any step was AI-assisted, and a `⋯` menu. The whole card opens the
 * template (edit, or a read-only view for a global row or a viewer without
 * write); the menu is Edit / Duplicate / Archive, or View / Duplicate when the
 * row can't be edited, plus Restore on an archived row that isn't global.
 */
import { ArrowCounterClockwise, Copy, DotsThree, Envelope, Eye, PencilSimple, Trash } from '../../../design-system/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { cn } from '../../../lib/utils';
import gmailLogo from '../../../assets/connectors/gmail.png';
import { PILL_BASE } from './OutreachStatusPill';
import { PILL_INDIGO } from './pillPalette';
import type { OutreachTemplate, OutreachTemplateKind } from '@discovr/types';

export const KIND_LABELS: Record<OutreachTemplateKind, string> = {
  sequence: 'First touch',
  after_reply: 'After reply',
  other: 'Other',
};

/** Formats `updatedAt` as e.g. "1 Sep" — `undefined` for an unparseable value
 *  so the caller can omit the meta line instead of throwing (RangeError from
 *  Intl.DateTimeFormat.format would otherwise take down the whole grid). */
function formatUpdatedAt(updatedAt: string): string | undefined {
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(date);
}

export interface TemplateCardProps {
  template: OutreachTemplate;
  canEdit: boolean;
  canFork: boolean;
  /** Whether the `⋯` menu renders at all — hidden without outreach:write. */
  showMenu: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onFork: () => void;
  onArchive: () => void;
  /** Un-archives the row; offered only on an archived, non-global row. */
  onRestore?: () => void;
}

export function TemplateCard({ template: t, canEdit, canFork, showMenu, onOpen, onEdit, onFork, onArchive, onRestore }: TemplateCardProps) {
  const updatedLabel = formatUpdatedAt(t.updatedAt);
  const firstStep = t.steps[0];
  return (
    <div
      data-template-card
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-(--beige-50) transition-shadow focus-within:ring-2 focus-within:ring-ring/40 hover:shadow-sm"
    >
      <button type="button" aria-label={`Open ${t.name}`} onClick={onOpen} className="absolute inset-0 z-0 cursor-pointer rounded-2xl outline-none" />
      {/* Figma 554:46136 Template Card: a 271px preview over an 86px footer. */}
      <div className="pointer-events-none flex min-h-[271px] flex-1 flex-col p-5">
        <div className="relative flex flex-1 flex-col gap-3 overflow-hidden rounded-lg border border-border bg-background p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Envelope size={13} /> Message
            </span>
            <span className={cn(PILL_BASE, PILL_INDIGO)}>{KIND_LABELS[t.kind]}</span>
          </div>
          {firstStep ? (
            <div className="min-w-0 flex-1 overflow-hidden">
              {firstStep.subject && <p className="truncate text-body-sm-medium text-foreground">{firstStep.subject}</p>}
              <p className="mt-1 line-clamp-7 whitespace-pre-wrap wrap-break-word text-body-sm-regular text-muted-foreground">{firstStep.body}</p>
            </div>
          ) : (
            <p className="flex-1 text-body-sm-regular text-muted-foreground">No content yet.</p>
          )}
          <div aria-hidden className="absolute inset-x-0 bottom-0 h-[77px] bg-gradient-to-t from-background to-transparent" />
        </div>
      </div>

      <div data-template-footer className="pointer-events-none relative flex items-center justify-between gap-3 border-t border-(--border-grey-subtle)! bg-card px-3 py-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-(--stone-200)!">
            <img src={gmailLogo} alt="Email" className="h-3.5 w-auto" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-[16px]/6 font-medium text-foreground">{t.name}</p>
            {(updatedLabel || t.archivedAt || t.aiAssisted) && (
              <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                {updatedLabel && <span>Updated {updatedLabel}</span>}
                {t.archivedAt && <span className="rounded-full bg-(--stone-100) px-2 py-0.5 text-[11px]">Archived</span>}
                {t.aiAssisted ? (
                  <span
                    title="AI helped write at least one step of this template"
                    className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700"
                  >
                    AI
                  </span>
                ) : null}
              </p>
            )}
          </div>
        </div>
        {showMenu ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Template actions for ${t.name}`}
                className="pointer-events-auto relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <DotsThree size={18} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {canEdit ? (
                <DropdownMenuItem onSelect={onEdit}>
                  <PencilSimple /> Edit
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={onOpen}>
                  <Eye /> View
                </DropdownMenuItem>
              )}
              {canFork ? (
                <DropdownMenuItem onSelect={onFork}>
                  <Copy /> Duplicate
                </DropdownMenuItem>
              ) : null}
              {t.archivedAt && onRestore ? (
                <DropdownMenuItem onSelect={onRestore}>
                  <ArrowCounterClockwise /> Restore
                </DropdownMenuItem>
              ) : null}
              {canEdit ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onSelect={onArchive}>
                    <Trash /> Archive
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}
